/**
 * plan/i18n-gaps — every API response passes through here (`lib/api.ts`). A field the server wrote as a
 * sentence and stored with its message beside it (`note` + `note_msg`, `set_by_name` + `set_by_msg`, …)
 * becomes a getter that gives the message in the language the screen is in *when it is read*, so a
 * toggle re-renders the right words without refetching, and no screen has to know which fields are
 * messages. A field without a message (older rows, typed text) is left exactly as the server sent it.
 *
 * The server says each message in both languages when it writes it (`{ k, p, th, en }`, API
 * `common/i18n/ui-text`), so the web needs no copy of the catalog — only the side to show.
 *
 * Plain module, no React: the API client is not a component.
 */
import { getDisplayLocale } from "@/lib/display-locale";

/** a message the server stored: which sentence, and the sentence in each language */
export interface StoredMsg {
  k: string;
  th: string;
  en: string;
}

export const isStoredMsg = (v: unknown): v is StoredMsg =>
  !!v && typeof v === "object" && typeof (v as StoredMsg).k === "string" && typeof (v as StoredMsg).th === "string" && typeof (v as StoredMsg).en === "string";

/** the message in the current UI language, or the fallback when the value is not a message */
export function sayStored(msg: unknown, fallback?: string | null, lang = getDisplayLocale()): string {
  return isStoredMsg(msg) ? msg[lang] : (fallback ?? "");
}

const MAX_DEPTH = 12;

function walk(v: unknown, depth: number): void {
  if (depth > MAX_DEPTH || !v || typeof v !== "object") return;
  if (Array.isArray(v)) {
    for (const x of v) walk(x, depth + 1);
    return;
  }
  const o = v as Record<string, unknown>;
  for (const key of Object.keys(o)) {
    const val = o[key];
    if (key.endsWith("_msg") && isStoredMsg(val)) {
      const base = key.slice(0, -4);
      // `set_by_msg` sits beside `set_by_name`, `by_msg` beside `by_name`
      const field = base in o ? base : `${base}_name` in o ? `${base}_name` : base;
      const current = o[field];
      if (current !== undefined && current !== null && typeof current !== "string") continue;
      Object.defineProperty(o, field, { get: () => val[getDisplayLocale()], enumerable: true, configurable: true });
    } else if (val && typeof val === "object") walk(val, depth + 1);
  }
}

/** says every stored message in the response in the screen's language (mutates and returns the same value) */
export function attachUiText<T>(data: T): T {
  walk(data, 0);
  return data;
}

/** "ข้อมูลเดิม" — a value seeded from before the system kept history (its stored Thai, or its message) */
export function isFromOldData(h: { set_by_name?: string | null; set_by_msg?: unknown }): boolean {
  return h.set_by_name === "ข้อมูลเดิม" || (isStoredMsg(h.set_by_msg) && h.set_by_msg.k === "fin.sys.oldData"); // i18n-ok: the stored value
}
